<template>
  <div class="bg-white min-h-screen">
    <div class="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-20">
        <p class="text-gray-500">Loading episode...</p>
      </div>
      <!-- Error State -->
      <div v-else-if="error" class="text-center py-20 max-w-2xl mx-auto">
        <h2 class="text-2xl font-bold text-red-700">Oops!</h2>
        <p class="mt-2 text-gray-600">{{ error }}</p>
         <router-link to="/" class="mt-6 inline-block text-brand-purple-dark hover:underline">Return to safety</router-link>
      </div>
      <!-- Main Content -->
      <div v-else-if="episode && client">
        <header class="mb-8">
           <router-link :to="`/shows/${client.slug}`" class="text-sm font-semibold text-brand-purple-dark hover:text-brand-pink-static">&larr; Back to All Episodes</router-link>
          
          <!-- START: NEW HEADER LAYOUT -->
          <div class="mt-4 flex flex-col sm:flex-row items-center gap-8">
            <div class="aspect-w-1 aspect-h-1 w-full max-w-[16rem] sm:w-64 flex-shrink-0">
              <img :src="episode.episodeCoverArtUrl || client.coverArtUrl" alt="Episode Cover Art" class="w-full h-full object-cover rounded-xl shadow-2xl">
            </div>
            <div class="text-center sm:text-left">
              <h1 class="text-4xl font-bold text-gray-900">{{ episode.title }}</h1>
              <p class="mt-2 text-lg text-gray-500">Published on {{ formatDate(episode.createdAt) }}</p>
            </div>
          </div>
          <!-- END: NEW HEADER LAYOUT -->

        </header>

        <main 
          class="mt-12 pt-8 border-t-4"
          :style="{ borderColor: client.brandColour || '#E7643B' }"
        >
          <div class="mb-8">
            <video controls :src="episode.mediaUrl" class="w-full rounded-md">
              Your browser does not support the video tag.
            </video>
          </div>

          <div class="mt-8">
            <h2 class="text-2xl font-semibold text-gray-800">Show Notes</h2>
            <div class="mt-4 prose max-w-none text-gray-700 whitespace-pre-wrap">{{ episode.showNotes }}</div>
          </div>
           <div v-if="episode.transcript" class="mt-8 border-t pt-6">
            <h2 class="text-2xl font-semibold text-gray-800">Transcript</h2>
            <div class="mt-4 p-4 bg-gray-50 rounded-md whitespace-pre-wrap text-gray-700 text-sm h-96 overflow-y-auto border">
              {{ episode.transcript }}
            </div>
          </div>
        </main>
      </div>
    </div>
    <footer class="text-center py-6">
      <a href="https://castive.app" target="_blank" rel="noopener noreferrer" class="inline-flex items-center space-x-2">
        <img src="@/assets/logo.png" alt="Castive Logo" class="h-5 w-auto opacity-75">
        <span class="text-sm text-gray-600 font-medium">Powered by Castive</span>
      </a>
    </footer>
  </div>
</template>

<script>
import { db } from '@/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

export default {
  name: 'EpisodeDetailView',
  data() {
    return {
      client: null,
      episode: null,
      loading: true,
      error: null,
    };
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp?.toDate) return '';
      const date = timestamp.toDate();
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Intl.DateTimeFormat('en-GB', options).format(date);
    },
  },
  async created() {
    try {
      const slug = this.$route.params.slug;
      const episodeId = this.$route.params.episodeId;
      if (!slug || !episodeId) throw new Error('Invalid URL. Missing podcast or episode identifier.');

      const episodeRef = doc(db, 'episodes', episodeId);
      const episodeSnap = await getDoc(episodeRef);
      if (!episodeSnap.exists()) {
        throw new Error('This episode could not be found.');
      }
      this.episode = episodeSnap.data();

      const clientsQuery = query(collection(db, 'clients'), where('slug', '==', slug));
      const clientSnapshot = await getDocs(clientsQuery);
      if (clientSnapshot.empty) {
        throw new Error('The parent podcast for this episode could not be found.');
      }
      const clientDoc = clientSnapshot.docs[0];
      this.client = { id: clientDoc.id, ...clientDoc.data() }; 

      if (this.episode.clientId !== this.client.id) {
        throw new Error("Episode does not belong to this podcast.");
      }

    } catch (err) {
      this.error = err.message;
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
.aspect-w-1 {
  aspect-ratio: 1 / 1;
}
</style>